import React, { useEffect, useState, useRef } from 'react';
import { arrayOf, bool, func } from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactList from 'react-list';
import { useFocusOnMount, withVisibilityLogic, ListNavigation } from 'mangools-react-components/src';

import shortcuts, { ARROW_DOWN, ARROW_UP, ENTER } from 'constants/Shortcuts';

import HistoryPanelItem from 'components/panels/historyPanel/HistoryPanelItem';

import sadImg from 'images/sad.svg';

import HistoryType from 'types/HistoryType';

const HistoryPanel = props => {
    const closeRef = useFocusOnMount();
    const listRef = useRef(null);
    const [preselectedItemId, setPreselectedItemId] = useState(null);

    useEffect(() => {
        props.onOpen();
    }, []);

    useEffect(() => {
        if (props.data.length > 0) {
            setPreselectedItemId(props.data[0].key);
        }
    }, [props.data]);

    const handleArrowUp = ([historyItem, newIndex]) => {
        setPreselectedItemId(historyItem.key);
        listRef.current.scrollAround(newIndex - 3);
    };

    const handleArrowDown = ([historyItem, newIndex]) => {
        setPreselectedItemId(historyItem.key);
        listRef.current.scrollAround(newIndex + 3);
    };

    const handleEnter = () => {
        const item = props.data.find(entry => entry.key === preselectedItemId);

        setPreselectedItemId(null);
        props.onClick({
            keyword: item.keyword,
            locationId: item.location.id,
            platformId: item.platformId,
        });
    };

    const renderEmptyListMessage = () => (
        <div className="mg-panel-tr uk-height-1-1 cursor-default">
            <div className="bg-lightgrey uk-flex uk-flex-middle uk-flex-center uk-text-center uk-height-1-1">
                <div>
                    <img src={sadImg} alt="Oh no" width="100" />
                    <br />
                    <br />
                    <strong className="font-18">Your history is empty</strong>
                    <br />
                    <br />
                    Start your SERP analysis now, your recent searches will be listed here.
                    <br />
                    <br />
                    <br />
                    <br />
                    <button
                        className="mg-btn is-white is-small"
                        onClick={props.onClose} // eslint-disable-line react/prop-types
                        type="button"
                        ref={closeRef}
                    >
                        Close this panel
                    </button>
                </div>
            </div>
        </div>
    );

    /* eslint-disable react/prop-types */
    const renderItem = (index, _key) => {
        const item = props.data[index];

        return (
            <HistoryPanelItem
                kdUpdatedAt={item.kdUpdatedAt}
                key={`${item.id}-${index}`}
                keyword={item.keyword}
                keywordDifficulty={item.keywordDifficulty}
                locationId={item.location.id}
                locationLabel={item.location.label}
                onClick={props.onClick}
                platformId={item.platformId}
                isPreselected={item.key === preselectedItemId}
            />
        );
    };
    /* eslint-enable react/prop-types */

    const renderItems = (items, ref) => <div ref={ref}>{items}</div>;

    const renderPreloaderItem = () => (
        <div className="mg-results-tr uk-flex" role="listitem">
            <div className="mg-results-td is-80 uk-flex uk-flex-middle">
                <span className="mg-preloader-block" style={{ width: '36px' }} />
            </div>
            <div className="mg-results-td uk-width-8-10 mg-truncate">
                <span className="font-16">
                    <span className="mg-preloader-block uk-width-7-10" />
                </span>
                <div>
                    <span className="mg-preloader-block uk-width-2-10" />
                </div>
            </div>
        </div>
    );

    const renderPreloader = () => (
        <div>
            {renderPreloaderItem()}
            {renderPreloaderItem()}
            {renderPreloaderItem()}
            {renderPreloaderItem()}
            {renderPreloaderItem()}
        </div>
    );

    const renderBody = () => {
        if (props.fetching) {
            return renderPreloader();
        } else if (props.data.length > 0) {
            return (
                <ListNavigation
                    data={props.data}
                    selectedItemId={preselectedItemId}
                    itemId="key"
                    onArrowDownShortcut={handleArrowDown}
                    onArrowUpShortcut={handleArrowUp}
                    onEnterShortcut={handleEnter}
                    shortcutKeys={[ARROW_DOWN, ARROW_UP, ENTER]}
                    shortcuts={shortcuts}
                >
                    <ReactList
                        itemRenderer={renderItem}
                        itemsRenderer={renderItems}
                        length={props.data.length}
                        type="uniform"
                        updateWhenDataChange={props.data}
                        ref={listRef}
                    />
                </ListNavigation>
            );
        } else {
            return renderEmptyListMessage();
        }
    };

    return (
        <div className="mg-panel">
            <div className="mg-panel-title mg-padding-30 mg-border-b">
                <div className="uk-flex uk-flex-space-between">
                    <h1 className="uk-margin-remove font-24">
                        <FontAwesomeIcon icon="history" aria-hidden="true" className="mg-margin-r-10" />
                        <strong>Search history</strong>
                    </h1>
                    <button
                        className="mg-btn is-xsmall is-white-red mg-margin-r-15 uk-hidden-small"
                        disabled={props.fetching || props.data.length === 0}
                        onClick={props.onDeleteHistory}
                        type="button"
                    >
                        <FontAwesomeIcon icon={['far', 'trash-alt']} aria-hidden="true" />
                        Clear history
                    </button>
                    <button className="mg-close" onClick={props.onClose} type="button">
                        <FontAwesomeIcon icon="times" />
                    </button>
                </div>
            </div>
            <div className="mg-panel-content">{renderBody()}</div>
        </div>
    );
};

HistoryPanel.propTypes = {
    data: arrayOf(HistoryType).isRequired,
    fetching: bool.isRequired,
    onClick: func.isRequired,
    onClose: func.isRequired,
    onOpen: func.isRequired,
    onDeleteHistory: func.isRequired,
};

export default withVisibilityLogic(HistoryPanel);
